import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, useMemo } from 'react';
import strings from '../../../Globalization';
import LinearChart from '../../../components/common/LinearChart';
import GaugeChart from '../../../components/common/charts/GaugeChart';
import consts from '../../../consts';
import { useCycleInfo } from '../../../contexts/CycleInfo/Context';
import util from '../../../util';

const PressureLossFlowCheck = ({ property = 'ENDOCHECK', propertyName }) => {
	const { data: cycle } = useCycleInfo();
	const theme = useTheme();
	const data = cycle.ChartDetail[property];
	const showLeakCharts = useMemo(
		() =>
			util.checkAccess('LeakTestAccess') &&
			data.LeakTestTopFlow.Value !== consts.DefaultValues.IntegerNull &&
			data.LeakTestBottomFlow.Value !== consts.DefaultValues.IntegerNull,
			[data]
	);

	const showFlowCheckCharts = useMemo(
		() =>
			util.checkAccess('FlowCheckAccess'),
		[]
	);
	const isEnspire = cycle.EquipmentFamilyId === consts.EquipmentFamilyType.ENSPIRE;
	
	const updateTopFlowCheck =
		isEnspire && 'TOPFLOWCHECK' + propertyName === 'TOPFLOWCHECKENDOCHECK'
			? `${strings.Top.toUpperCase()} ${strings.ChannelMonitor.toUpperCase()}`
			: isEnspire && 'TOPFLOWCHECK' + propertyName === 'TOPFLOWCHECKENDOCOMPLETIONCHECK'
			? strings['TOPCHANNELMONITORONCOMPLETE']
			: strings['TOPFLOWCHECK' + propertyName];
	const updateBottomFlowCheck =
		isEnspire && 'BOTTOMFLOWCHECK' + propertyName === 'BOTTOMFLOWCHECKENDOCHECK'
			? `${strings.Bottom.toUpperCase()} ${strings.ChannelMonitor.toUpperCase()}`
			: isEnspire && 'BOTTOMFLOWCHECK' + propertyName === 'BOTTOMFLOWCHECKENDOCOMPLETIONCHECK'
			? strings['BOTTOMCHANNELMONITORONCOMPLETE']
			: strings['BOTTOMFLOWCHECK' + propertyName];
	const updatedChartName =
		showLeakCharts && isEnspire && 'PRESSURELOSS' + propertyName === 'PRESSURELOSSENDOCHECK'
			? `${strings.PressureMonitor.toUpperCase()} ${strings.PressureLost.toUpperCase()}`
			: strings['PRESSURELOSS' + propertyName];

	const [leakTestTopFlowMax, leakTestBottomFlowMax] = useMemo(
		() => [
			Math.max(data.LeakTestTopFlow.Max, data.LeakTestTopFlow.Value) + 10,
			Math.max(data.LeakTestBottomFlow.Max, data.LeakTestBottomFlow.Value) + 10
		],
		[data.LeakTestTopFlow, data.LeakTestBottomFlow]
	);
	return (
		<Grid container>
			<Grid size={12}>
				<Card>
					<CardContent sx={{ paddingTop: 4, paddingBottom: 4 }}>
						<Grid container>
							{showLeakCharts && (
								<Grid size={{xs: 12, lg: 4}}  padding={1}>
									<Box
										display={'flex'}
										alignItems={'center'}
										sx={{
											borderTop: (theme) => `1px solid ${theme.palette.divider}`,
											borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
											padding: (theme) => theme.spacing(1)
										}}
									>
										<Typography variant="subtitle2" flex={1}>
											{updatedChartName} ({data.LeakTestTopFlow.Unit})
										</Typography>
										<TimerOutlinedIcon />
									</Box>
									<Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
										<GaugeChart
											min={-10}
											max={leakTestTopFlowMax}
											value={data.LeakTestTopFlow.Value}
											tickCount={21}
											tickdecimals={2}
											startAngle={-90}
											endAngle={90}
											labelTickCount={5}
											pointerLabel={data.LeakTestTopFlow.Unit}
											uom={data.LeakTestTopFlow.Value}
											title={strings.Top}
											segments={[
												{
													min: -10,
													max: data.LeakTestTopFlow.Max,
													color: theme.palette.success.light
												},
												{
													min: data.LeakTestTopFlow.Max,
													max: leakTestTopFlowMax,
													color: theme.palette.error.light
												}
											]}
											gradient
										/>
									</Box>

									<Box
										display={'flex'}
										alignItems={'center'}
										sx={{
											borderTop: (theme) => `1px solid ${theme.palette.divider}`,
											borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
											padding: (theme) => theme.spacing(1)
										}}
									>
										<Typography variant="subtitle2" flex={1}>
											{updatedChartName} ({data.LeakTestBottomFlow.Unit})
										</Typography>
										<TimerOutlinedIcon />
									</Box>
									<Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
										<GaugeChart
											min={-10}
											max={leakTestBottomFlowMax}
											value={data.LeakTestBottomFlow.Value}
											tickCount={21}
											startAngle={-90}
											endAngle={90}
											labelTickCount={5}
											pointerLabel={data.LeakTestBottomFlow.Unit}
											uom={data.LeakTestBottomFlow.Value}
											title={strings.Bottom}
											segments={[
												{
													min: -10,
													max: data.LeakTestBottomFlow.Max,
													color: theme.palette.success.light
												},
												{
													min: data.LeakTestBottomFlow.Max,
													max: leakTestBottomFlowMax,
													color: theme.palette.error.light
												}
											]}
											gradient
										/>
									</Box>
								</Grid>
							)}
							{showFlowCheckCharts && (
								<Grid size={{xs: 12, lg: 4}}  padding={1}>
									<Box
										display={'flex'}
										flexDirection={'column'}
										sx={{
											borderTop: (theme) => `1px solid ${theme.palette.divider}`,
											borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
											padding: (theme) => theme.spacing(1)
										}}
									>
										<Typography variant="subtitle2">{updateTopFlowCheck}</Typography>
									</Box>
									<Box display={'flex'} alignItems={'center'} flexDirection={'column'} justifyContent={'center'}>
										{Object.keys(data.TopFlow).map((value, index) => (
											<LinearChart
												key={index}
												title=""
												iconWidth="10%"
												seriesName=""
												upperLimit={5}
												value={data.TopFlow[value]}
											/>
										))}
									</Box>
								</Grid>
							)}

							{showFlowCheckCharts && (
								<Grid size={{xs: 12, lg: 4}}  padding={1}>
									<Box
										display={'flex'}
										flexDirection={'column'}
										sx={{
											borderTop: (theme) => `1px solid ${theme.palette.divider}`,
											borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
											padding: (theme) => theme.spacing(1)
										}}
									>
										<Typography variant="subtitle2">{updateBottomFlowCheck}</Typography>
									</Box>
									<Box display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
										{Object.keys(data.BottomFlow).map((value, index) => (
											<LinearChart
												key={index}
												title=""
												iconWidth="10%"
												seriesName=""
												upperLimit={5}
												value={data.BottomFlow[value]}
											/>
										))}
									</Box>
								</Grid>
							)}
						</Grid>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

export default memo(PressureLossFlowCheck);